import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Pencil, Plus, Trash2, User, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { CartaoVerticais, CampoVerticais, useVerticaisDaCriacao, verticaisDaCriacao } from '../../componentes/verticais';
import { Botao, Cartao } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { BuscaRemota, Formulario, Rotulo, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post, getTodos } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { PadroesDepartamento } from '../../nucleo/notificacoes/PadroesPapel';
import { limpar, type Departamento, type GestorDepartamento } from './tipos';
import { nomePessoa } from '../../nucleo/formato';

/** Lote de 19/10: teto de gestores por departamento (o mesmo do servidor). */
export const MAX_GESTORES = 10;
/** Quantos nomes de gestor a linha mostra antes do "+N". */
const GESTORES_A_VISTA = 3;

/** Gestores do departamento; resposta antiga (sem `gestores`) cai no gestor único. */
// Val5 (29/10): departamentos são estrutura de Pessoas (`pessoas.configurar`), inclusive excluir.
export function gestoresDe(d: Pick<Departamento, 'gestores' | 'gestor_funcionario_id' | 'gestor_nome'>): GestorDepartamento[] {
  if (Array.isArray(d.gestores)) return d.gestores;
  return d.gestor_funcionario_id && d.gestor_nome ? [{ id: d.gestor_funcionario_id, nome: d.gestor_nome }] : [];
}

/** Nomes dos gestores na linha: até três pelo `nomePessoa` e "+N" para o resto. */
export function GestoresNaLinha({ gestores }: { gestores: GestorDepartamento[] }) {
  const { t } = useTranslation('pessoas');
  if (!gestores.length) return null;
  const resto = gestores.length - GESTORES_A_VISTA;
  return (
    <span className="inline-flex min-w-0 items-center gap-1 text-xs text-tinta-3">
      <User className="size-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">{gestores.slice(0, GESTORES_A_VISTA).map((g) => nomePessoa(g.nome)).join(', ')}</span>
      {resto > 0 && <span className="numero shrink-0" aria-label={t('departamentos.outrosGestores', { count: resto })}>+{resto}</span>}
    </span>
  );
}

/** Lote de 19/10: os gestores do departamento, em ordem, com a busca de funcionário ativo para acrescentar (até 10). */
export function CampoGestores({ valor, aoMudar }: { valor: GestorDepartamento[]; aoMudar: (v: GestorDepartamento[]) => void }) {
  const { t } = useTranslation('pessoas');
  const cheio = valor.length >= MAX_GESTORES;
  const incluir = (g: GestorDepartamento) => {
    if (cheio || valor.some((x) => x.id === g.id)) return;
    aoMudar([...valor, g]);
  };
  return (
    <div className="flex flex-col gap-3 sm:col-span-6">
      <span className="text-sm font-medium text-tinta-2">{t('campos.gestoresDepartamento')}</span>
      {valor.length ? (
        <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
          {valor.map((g) => (
            <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="min-w-0 grow basis-24 font-medium break-words text-tinta-1">{nomePessoa(g.nome)}</span>
              <Botao variante="fantasma" tamanho="sm" icone={<X className="size-4" />} aria-label={t('departamentos.removerGestor', { nome: nomePessoa(g.nome) })} onClick={() => aoMudar(valor.filter((x) => x.id !== g.id))} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-tinta-3">{t('departamentos.semGestores')}</p>
      )}
      {cheio ? (
        <p className="text-sm text-tinta-3">{t('departamentos.limiteGestores', { max: MAX_GESTORES })}</p>
      ) : (
        <Rotulo id="departamento-gestor-novo" rotulo={t('departamentos.adicionarGestor')}>
          <BuscaRemota
            id="departamento-gestor-novo"
            def={{ caminho: '/pessoas/funcionarios', params: { estado: 'ativo' }, rotulo: (i) => String(i.nome ?? ''), pessoa: true }}
            valor={null}
            aoMudar={(v, item) => v && incluir({ id: v, nome: String(item?.nome ?? '') })}
          />
        </Rotulo>
      )}
    </div>
  );
}

interface No extends Departamento {
  filhos: No[];
}

/** Monta a árvore; quem tem pai que não veio na lista vira raiz. */
function montarArvore(itens: Departamento[]): No[] {
  const porId = new Map<string, No>(itens.map((d) => [d.id, { ...d, filhos: [] }]));
  const raizes: No[] = [];
  for (const d of porId.values()) {
    const pai = d.parent_id ? porId.get(d.parent_id) : undefined;
    if (pai) pai.filhos.push(d);
    else raizes.push(d);
  }
  return raizes;
}

/** M7.2: departamentos em árvore, com gestor e totais. */
export function Departamentos() {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Departamento | 'novo' | null>(null);
  const [remover, setRemover] = useState<Departamento | null>(null);
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  // Ficha 2026-10-22: as verticais já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useVerticaisDaCriacao(aberto);
  const [gestores, setGestores] = useState<GestorDepartamento[]>([]);
  const abrir = (d: Departamento | 'novo') => {
    setGestores(d === 'novo' ? [] : gestoresDe(d));
    setAberto(d);
  };
  const q = useQuery({ queryKey: ['pessoas', 'departamentos'], queryFn: () => getTodos<Departamento>('/pessoas/departamentos') });
  const arvore = useMemo(() => montarArvore(q.data?.itens ?? []), [q.data]);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pessoas'] });

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pessoas/departamentos/${id}`),
    onSuccess: async () => {
      setRemover(null);
      await recarregar();
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'parent_id', rotulo: t('campos.departamentoPai'), tipo: 'busca', largura: 'meia', busca: { caminho: '/pessoas/departamentos', rotulo: (i) => String(i.nome) } },
  ];

  const linhas = (nos: No[], nivel: number): React.ReactNode =>
    nos.map((d) => (
      <li key={d.id}>
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-borda py-2.5 pr-1 text-sm" style={{ paddingLeft: `${nivel * 1.5}rem` }}>
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="size-4 shrink-0 text-tinta-3" aria-hidden />
            <span className="truncate font-medium" title={d.nome}>{d.nome}</span>
          </span>
          <span className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
            <GestoresNaLinha gestores={gestoresDe(d)} />
            <span className="numero text-xs text-tinta-3">{t('departamentos.pessoas', { count: d.funcionarios })}, {t('departamentos.funcoes', { count: d.funcoes })}</span>
            {s.pode('pessoas.configurar') && (
              <button type="button" aria-label={tn('comum.editar')} onClick={() => abrir(d)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
                <Pencil className="size-4" />
              </button>
            )}
            {s.pode('pessoas.configurar') && (
              <button type="button" aria-label={tn('comum.excluir')} onClick={() => setRemover(d)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                <Trash2 className="size-4" />
              </button>
            )}
          </span>
        </div>
        {d.filhos.length > 0 && <ul>{linhas(d.filhos, nivel + 1)}</ul>}
      </li>
    ));

  return (
    <>
      {s.pode('pessoas.configurar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => abrir('novo')}>
            {t('acoes.novoDepartamento')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Cartao semPadding>
        <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
          {() => <ul className="px-4">{linhas(arvore, 0)}</ul>}
        </Estado>
      </Cartao>
      <Modal
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? atual.nome : t('acoes.novoDepartamento')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-departamento">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-departamento"
          campos={campos}
          inicial={(atual ?? {}) as unknown as Record<string, unknown>}
          rotulosAtuais={atual ? { parent_id: atual.parent_nome ?? '' } : undefined}
          aoEnviar={async (v) => {
            const corpo = { ...limpar({ nome: v.nome, parent_id: v.parent_id }), gestor_ids: gestores.map((g) => g.id) };
            if (atual) await patch(`/pessoas/departamentos/${atual.id}`, corpo);
            else await post('/pessoas/departamentos', { ...corpo, ...verticaisDaCriacao(verticais) });
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        >
          {!atual && <CampoVerticais valor={verticais} aoMudar={setVerticais} />}
          <CampoGestores valor={gestores} aoMudar={setGestores} />
        </Formulario>
        {atual && (
          <div className="mt-4">
            <CartaoVerticais entidade="pessoas.departamento" registroId={atual.id} podeEditar={s.pode('pessoas.configurar')} comHistorico />
          </div>
        )}
        {/* Lote de 21/10 (item 2): padrão de aviso do departamento (antes do papel); muda quem também muda papéis. */}
        {atual && s.pode('admin.papeis') && (
          <div className="mt-6 border-t border-borda pt-4">
            <PadroesDepartamento departamentoId={atual.id} editavel />
          </div>
        )}
      </Modal>
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.excluirDepartamento')} texto={t('confirmar.excluirDepartamentoTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
